import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AREAS, BOOKING_URL, SELF_CHECK } from './content';
import { Arrow, Kicker, Reveal, Scorecard, isPrerender } from './parts';

const CALL_POINTS = [
  'You tell us what’s eating the most time.',
  'We ask how the work actually moves today.',
  'We tell you straight whether an audit is worth doing.',
];

export function Book() {
  const [selfCheckOpen, setSelfCheckOpen] = useState(false);

  return (
    <section id="book" className="rb-night px-5 sm:px-8 py-16 sm:py-24 scroll-mt-4">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-14 items-start">
        <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
          <Kicker>Book your free call</Kicker>
          <h2 className="rb-display mt-6 text-[1.95rem] sm:text-[3rem] leading-[1.06] font-[400] text-white">
            Start with a <span className="rb-accent"><span className="whitespace-nowrap">30-minute</span> conversation.</span>
          </h2>
          <p className="mt-6 text-[1.05rem] leading-relaxed text-[#b9bcd4]">
            Free, no slides, no pitch. Here’s what happens on the call:
          </p>
          <ol className="mt-6 space-y-4">
            {CALL_POINTS.map((p, i) => (
              <li key={p} className="flex gap-4 text-[1rem] text-white/90">
                <span className="text-[0.9rem] font-semibold text-brand-light mt-0.5">0{i + 1}</span>
                {p}
              </li>
            ))}
          </ol>

          <div className="mt-8 rounded-2xl border border-white/15 p-6">
            <div className="text-white font-semibold">Not ready to talk yet?</div>
            <p className="mt-1.5 text-[0.95rem] text-[#b9bcd4]">
              Answer six quick questions and get a rough scorecard for your business in about two minutes.
            </p>
            <button
              type="button"
              onClick={() => setSelfCheckOpen(true)}
              className="mt-3 min-h-[44px] inline-flex items-center gap-2 font-semibold text-brand-light hover:text-white transition-colors"
            >
              Take the 2-minute self-check <Arrow />
            </button>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.08}>
          <CalendlyEmbed />
        </Reveal>
      </div>

      <SelfCheck open={selfCheckOpen} onClose={() => setSelfCheckOpen(false)} />
    </section>
  );
}

// The Calendly iframe is mounted only in the visitor's browser (never baked into the prerendered HTML,
// which would otherwise hard-code the build machine's hostname into embed_domain). A loading message
// sits behind it until the frame reports it has loaded; if that takes too long, the new-tab link is
// promoted into the box itself.
function CalendlyEmbed() {
  const boxRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [slow, setSlow] = useState(false);

  // Start loading once the box is within ~a screen of the viewport (replaces loading="lazy", so the
  // "taking a while" timer only starts when the visitor is actually heading there).
  useEffect(() => {
    const el = boxRef.current;
    if (!el || isPrerender()) return undefined;
    const url = `${BOOKING_URL}?embed_type=Inline&embed_domain=${window.location.hostname}&hide_gdpr_banner=1&background_color=fbf9f5&text_color=161a3a&primary_color=34507a`;
    if (!('IntersectionObserver' in window)) {
      setSrc(url);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSrc(url);
          io.disconnect();
        }
      },
      { rootMargin: '900px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!src || loaded) return undefined;
    const t = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(t);
  }, [src, loaded]);

  // The iframe's load event fires before Calendly has drawn the calendar, so wait for Calendly's own
  // postMessage (it sends calendly.* events once the scheduler is on screen). If none arrives within a
  // few seconds of the frame loading, show the frame anyway rather than hide a working calendar.
  useEffect(() => {
    if (!src || loaded) return undefined;
    const onMessage = (e) => {
      if (/^https:\/\/([a-z0-9-]+\.)?calendly\.com$/.test(e.origin) && typeof e.data?.event === 'string' && e.data.event.startsWith('calendly.')) {
        setLoaded(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [src, loaded]);

  const onFrameLoad = () => {
    setTimeout(() => setLoaded(true), 4000);
  };

  return (
    <>
      <div ref={boxRef} className="relative rounded-[20px] overflow-hidden bg-paper-card shadow-[0_40px_80px_-40px_rgba(0,0,0,0.6)] h-[720px] sm:h-[700px]">
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center" role="status">
            <span className="rb-spinner" aria-hidden="true" />
            <p className="text-[1rem] font-semibold text-ink">Loading the booking calendar…</p>
            {slow && (
              <p className="text-[0.95rem] text-ink-soft max-w-xs">
                This is taking a while.{' '}
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="rb-link">
                  Open the calendar in a new tab
                </a>
              </p>
            )}
          </div>
        )}
        {src && (
          <iframe
            title="Book a free 30-minute call with Primeslink"
            src={src}
            className={`relative w-full h-full block transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={onFrameLoad}
          />
        )}
      </div>
      <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 min-h-[44px] inline-flex items-center gap-2 text-[0.95rem] font-semibold text-brand-light hover:text-white transition-colors"
      >
        Calendar not loading? Open it in a new tab <Arrow />
      </a>
    </>
  );
}

function SelfCheck({ open, onClose }) {
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const done = step >= SELF_CHECK.length;
  const scores = SELF_CHECK.map((q) => answers[q.area] ?? 0);
  const lowestIdx = scores.indexOf(Math.min(...scores));

  const choose = (area, score) => {
    setAnswers((a) => ({ ...a, [area]: score }));
    setStep((s) => s + 1);
  };
  const restart = () => {
    setAnswers({});
    setStep(0);
  };
  const goBook = () => {
    onClose();
    document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="rb fixed inset-0 z-[60] !min-h-0 !bg-transparent flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label="Two-minute self-check"
        >
          <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-lg rounded-[22px] bg-paper p-6 sm:p-8 my-8 shadow-2xl"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full border border-ink/15 text-ink"
              aria-label="Close"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>

            {!done ? (
              <>
                <div className="rb-label">
                  Self-check · {step + 1} of {SELF_CHECK.length} · {SELF_CHECK[step].area}
                </div>
                <div className="mt-3 rb-bar">
                  <span style={{ width: `${(step / SELF_CHECK.length) * 100}%`, transition: 'width .4s ease' }} />
                </div>
                <h3 className="rb-display text-[1.6rem] leading-snug mt-6 pr-6">{SELF_CHECK[step].q}</h3>
                <div className="mt-6 space-y-3">
                  {SELF_CHECK[step].options.map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      onClick={() => choose(SELF_CHECK[step].area, o.score)}
                      className="w-full text-left rb-card px-5 py-4 text-[1rem] text-ink hover:border-brand hover:bg-white transition-colors"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button type="button" onClick={() => setStep((s) => s - 1)} className="mt-5 text-[0.9rem] text-ink-mute hover:text-ink">
                    ← Back
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="rb-label">Your rough scorecard</div>
                <h3 className="rb-display text-[1.6rem] leading-snug mt-3 pr-6">
                  Your biggest leak looks like <span className="rb-accent">{AREAS[lowestIdx].toLowerCase()}.</span>
                </h3>
                <div className="mt-6">
                  <Scorecard
                    compact
                    title="Self-check"
                    subtitle="Based on your six answers"
                    scores={scores}
                    footnote="A rough self-check, not the audit. The real audit looks at how the work actually moves."
                  />
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-5">
                  <button type="button" onClick={goBook} className="rb-btn">
                    Book your free call <Arrow />
                  </button>
                  <button type="button" onClick={restart} className="text-[0.92rem] text-ink-mute hover:text-ink">
                    Start over
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
